// AI Chat Panel — docked column in the editor shell

import type { EditorApp } from './EditorApp';
import {
  type AISettings, type AIMessage, type ContentBlock, type ProviderType,
  loadAISettings, saveAISettings, clearAISettings, getDefaultModel,
  streamChat, validateKey, resizeImageToBase64, resizeDataUrlToBase64,
  PROVIDER_CONFIGS,
} from './aiProvider';
import { buildSystemPrompt, buildContextMessage } from './aiPrompt';
import { diffLines, diffStats, diffHunks } from './lineDiff';

// --- History data model ---

const HISTORY_KEY = 'scadplus-ai-history';
const VIEW_KEY = 'scadplus-ai-attach-view';
const AUTOFIX_KEY = 'scadplus-ai-autofix';
const MAX_HISTORY = 20;

interface ConversationSummary {
  id: string;
  title: string;
  timestamp: number;
  messages: AIMessage[];
}

// --- Syntax highlighting token sets ---

const SH_KEYWORDS = new Set([
  'module', 'function', 'if', 'else', 'for', 'let', 'each', 'include', 'use',
]);

const SH_BUILTINS = new Set([
  // Shapes
  'cube', 'sphere', 'cylinder', 'polyhedron', 'torus', 'pipe', 'ellipsoid',
  'wedge', 'capsule', 'rounded_cube', 'chamfered_cylinder', 'thread', 'spring',
  // 2D
  'circle', 'square', 'polygon', 'regular_polygon', 'hexagon', 'slot', 'star', 'text',
  // Transforms
  'translate', 'rotate', 'scale', 'mirror', 'resize', 'offset', 'color',
  // Booleans
  'union', 'difference', 'intersection', 'hull', 'minkowski',
  // Extrusions
  'linear_extrude', 'rotate_extrude',
  // Layout
  'align', 'attach', 'position', 'group', 'frame',
  // Hardware
  'bolt', 'nut', 'standoff', 'enclosure', 'shell',
  // Gears
  'spur_gear', 'helical_gear', 'herringbone_gear', 'rack', 'bevel_gear', 'worm',
  // Profiles
  'l_profile', 'u_profile', 'o_profile', 't_profile', 'i_profile',
  // Math
  'sin', 'cos', 'tan', 'abs', 'ceil', 'floor', 'round', 'sqrt', 'pow',
  'min', 'max', 'ln', 'log', 'len', 'concat',
  // Other
  'children', 'import',
]);

const SH_CONSTANTS = new Set([
  'true', 'false', 'undef',
  'CENTER', 'TOP', 'BOTTOM', 'LEFT', 'RIGHT', 'FRONT', 'BACK',
  'PI', 'X', 'Y', 'Z', 'XY', 'XZ', 'YZ',
]);

export class AIChatPanel {
  private container: HTMLElement;
  private app: EditorApp;
  private panel: HTMLElement;
  private messageList: HTMLElement;
  private textInput: HTMLTextAreaElement;
  private sendBtn: HTMLButtonElement;
  private stopBtn: HTMLButtonElement;
  private imageBtn: HTMLButtonElement;
  private imageInput: HTMLInputElement;
  private newChatBtn: HTMLButtonElement;
  private messages: AIMessage[] = [];
  private currentAbort: AbortController | null = null;
  private streamingEl: HTMLElement | null = null;
  private streamingText = '';
  private pendingImages: { data: string; mimeType: string }[] = [];
  private imagePreviewContainer: HTMLElement;
  private lastErrors: string[] = [];
  private lastEcho: string[] = [];
  private viewToggle: HTMLInputElement;
  private autoFixToggle: HTMLInputElement;
  /** Remaining automatic "please fix these errors" round-trips for the current request. */
  private autoFixBudget = 0;
  /** An AI change was just applied; check the next render for errors. */
  private awaitingAppliedRender = false;
  private lastHadScreenshot = false;

  // Tabs & history
  private activeTab: 'chat' | 'history' = 'chat';
  private tabBar: HTMLElement;
  private historyList: HTMLElement;
  private currentConversationId: string | null = null;

  constructor(mountPoint: HTMLElement, app: EditorApp) {
    this.container = mountPoint;
    this.app = app;
    this.panel = this.buildPanel();
    this.messageList = this.panel.querySelector('.ai-messages')!;
    this.textInput = this.panel.querySelector('.ai-input-text') as HTMLTextAreaElement;
    this.sendBtn = this.panel.querySelector('.ai-send-btn') as HTMLButtonElement;
    this.stopBtn = this.panel.querySelector('.ai-stop-btn') as HTMLButtonElement;
    this.imageBtn = this.panel.querySelector('.ai-image-btn') as HTMLButtonElement;
    this.imageInput = this.panel.querySelector('.ai-image-input') as HTMLInputElement;
    this.newChatBtn = this.panel.querySelector('.ai-new-chat') as HTMLButtonElement;
    this.imagePreviewContainer = this.panel.querySelector('.ai-image-previews') as HTMLElement;
    this.tabBar = this.panel.querySelector('.ai-tab-bar')!;
    this.historyList = this.panel.querySelector('.ai-history-list')!;
    this.viewToggle = this.panel.querySelector('.ai-view-toggle') as HTMLInputElement;
    this.autoFixToggle = this.panel.querySelector('.ai-autofix-toggle') as HTMLInputElement;
    for (const [toggle, key] of [[this.viewToggle, VIEW_KEY], [this.autoFixToggle, AUTOFIX_KEY]] as const) {
      try { toggle.checked = localStorage.getItem(key) === '1'; } catch { /* storage unavailable */ }
      toggle.addEventListener('change', () => {
        try { localStorage.setItem(key, toggle.checked ? '1' : '0'); } catch { /* storage unavailable */ }
      });
    }
    window.addEventListener('scadplus:render-complete', (e) => {
      const detail = (e as CustomEvent<{ errors: Array<{ message: string; severity: string; from: { line: number; col: number } }> }>).detail;
      this.onRenderComplete(detail?.errors ?? []);
    });

    this.bindEvents();
    this.updateProviderLabel();
    mountPoint.appendChild(this.panel);
  }

  private buildPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.id = 'ai-chat-panel';
    panel.classList.add('docked');

    panel.innerHTML = `
      <div class="ai-head">
        <div class="title">Assistant</div>
        <div class="acts">
          <button class="ai-tab-btn" data-tab="history" title="History">History</button>
          <button class="ai-new-chat" title="New chat">+ New</button>
          <button class="ai-settings-btn" title="AI settings">&#9881;</button>
        </div>
      </div>
      <div class="ai-provider">
        <span class="prov"><span class="prov-label">Not configured</span></span>
        <span class="model"></span>
      </div>
      <div class="ai-tab-bar" style="display:none">
        <button class="ai-tab active" data-tab="chat">Chat</button>
        <button class="ai-tab" data-tab="history">History</button>
      </div>
      <div class="ai-thread ai-messages"></div>
      <div class="ai-history-list" style="display:none"></div>
      <div class="ai-image-previews"></div>
      <div class="ai-compose">
        <textarea class="ai-input-text" placeholder="Ask about your model — paste an image, describe a change…" rows="2"></textarea>
        <div class="ai-compose-foot">
          <span class="ctx ai-input-row-controls">
            <button class="ai-image-btn" title="Upload image">&#128206;</button>
            <label class="ai-opt ai-view-opt" title="Attach a screenshot of the 3D view to each message"><input type="checkbox" class="ai-view-toggle"> view</label>
            <label class="ai-opt" title="After you apply AI code, send any render errors back automatically (max 2 tries)"><input type="checkbox" class="ai-autofix-toggle"> auto-fix</label>
            <span class="ai-ctx-label">Context: <b class="ai-ctx-filename">model</b></span>
          </span>
          <div class="ai-compose-actions">
            <button class="ai-stop-btn" title="Stop generating" style="display:none">Stop</button>
            <button class="ai-send-btn send" title="Send">SEND &#8629;</button>
          </div>
        </div>
        <input type="file" class="ai-image-input" accept=".png,.jpg,.jpeg,.webp" style="display:none">
      </div>
    `;
    return panel;
  }

  private updateProviderLabel(): void {
    const settings = loadAISettings();
    const provLabel = this.panel.querySelector('.prov-label') as HTMLElement | null;
    const modelLabel = this.panel.querySelector('.model') as HTMLElement | null;
    const provBadge = this.panel.querySelector('.prov') as HTMLElement | null;
    if (!provLabel || !modelLabel || !provBadge) return;
    if (settings) {
      const cfg = PROVIDER_CONFIGS[settings.provider];
      provLabel.textContent = cfg.label;
      modelLabel.textContent = settings.model || getDefaultModel(settings.provider);
      provBadge.classList.toggle('unconfigured', false);
      (this.panel.querySelector('.ai-view-opt') as HTMLElement).hidden = !cfg.supportsImages;
    } else {
      provLabel.textContent = 'Not configured';
      modelLabel.textContent = '';
      provBadge.classList.toggle('unconfigured', true);
    }
  }

  private bindEvents(): void {
    // Send
    this.sendBtn.addEventListener('click', () => void this.sendMessage());
    this.textInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void this.sendMessage();
      }
    });

    // Stop
    this.stopBtn.addEventListener('click', () => this.stopStreaming());

    // Image upload
    this.imageBtn.addEventListener('click', () => this.imageInput.click());
    this.imageInput.addEventListener('change', () => this.handleImageUpload());

    // Image paste
    this.textInput.addEventListener('paste', (e) => this.handleImagePaste(e));

    // New chat
    this.newChatBtn.addEventListener('click', () => {
      this.clearChat();
      if (this.activeTab !== 'chat') this.switchTab('chat');
    });

    // Auto-resize textarea
    this.textInput.addEventListener('input', () => {
      this.textInput.style.height = 'auto';
      this.textInput.style.height = Math.min(this.textInput.scrollHeight, 120) + 'px';
    });

    // History toggle in header
    const historyBtn = this.panel.querySelector('.ai-tab-btn[data-tab="history"]') as HTMLButtonElement | null;
    if (historyBtn) {
      historyBtn.addEventListener('click', () => {
        const next = this.activeTab === 'history' ? 'chat' : 'history';
        this.switchTab(next);
        historyBtn.classList.toggle('active', next === 'history');
      });
    }

    // Settings gear — dispatches custom event main.ts listens for
    const settingsBtn = this.panel.querySelector('.ai-settings-btn') as HTMLButtonElement | null;
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => {
        this.panel.dispatchEvent(new CustomEvent('ai-open-settings', { bubbles: true }));
      });
    }
  }

  // --- Tab switching ---

  private switchTab(tab: 'chat' | 'history'): void {
    this.activeTab = tab;
    this.tabBar.querySelectorAll('.ai-tab').forEach(btn => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.tab === tab);
    });
    const historyBtn = this.panel.querySelector('.ai-tab-btn[data-tab="history"]') as HTMLButtonElement | null;
    if (historyBtn) historyBtn.classList.toggle('active', tab === 'history');

    const compose = this.panel.querySelector('.ai-compose') as HTMLElement | null;
    if (tab === 'chat') {
      this.messageList.style.display = '';
      this.historyList.style.display = 'none';
      this.imagePreviewContainer.style.display = '';
      if (compose) compose.style.display = '';
    } else {
      this.messageList.style.display = 'none';
      this.historyList.style.display = '';
      this.imagePreviewContainer.style.display = 'none';
      if (compose) compose.style.display = 'none';
      this.renderHistoryList();
    }
  }

  // --- Public API ---

  toggle(): void {
    if (this.panel.classList.contains('hidden')) {
      this.open();
    } else {
      this.close();
    }
  }

  open(): void {
    this.panel.classList.remove('hidden');
    this.textInput.focus();
  }

  close(): void {
    this.saveCurrentToHistory();
    this.panel.classList.add('hidden');
  }

  isOpen(): boolean {
    return !this.panel.classList.contains('hidden');
  }

  isConfigured(): boolean {
    const settings = loadAISettings();
    if (!settings) return false;
    const config = PROVIDER_CONFIGS[settings.provider];
    return !config.needsAuth || settings.apiKey.length > 0;
  }

  refreshProvider(): void {
    this.updateProviderLabel();
  }

  setErrors(errors: Array<{ message: string; severity?: string; from?: { line: number; col: number } }>): void {
    this.lastErrors = errors.map(e =>
      `${e.from ? `line ${e.from.line}:${e.from.col} ` : ''}${e.severity === 'warning' ? '(warning) ' : ''}${e.message}`);
  }

  /** Echo/console output of the last render (last 20 lines are sent as context). */
  setEchoLog(echo: string[]): void {
    this.lastEcho = echo.slice(-20);
  }

  /** Ask the assistant to fix the current errors (console "Fix with AI"). */
  fixErrors(): void {
    if (!this.isOpen()) this.open();
    if (this.activeTab !== 'chat') this.switchTab('chat');
    if (!this.isConfigured()) {
      this.panel.dispatchEvent(new CustomEvent('ai-open-settings', { bubbles: true }));
      return;
    }
    void this.sendMessage('Please fix the errors in the current code. Keep everything else unchanged.');
  }

  private onRenderComplete(errors: Array<{ message: string; severity: string; from: { line: number; col: number } }>): void {
    if (!this.awaitingAppliedRender) return;
    this.awaitingAppliedRender = false;
    const real = errors.filter(e => e.severity !== 'warning');
    if (real.length === 0 || !this.autoFixToggle.checked || this.autoFixBudget <= 0 || this.currentAbort) return;
    this.autoFixBudget--;
    const list = real.slice(0, 10).map(e => `- line ${e.from.line}:${e.from.col} ${e.message}`).join('\n');
    void this.sendMessage(`The code you gave produces these errors:\n${list}\nPlease fix them.`, { auto: true });
  }

  // --- Chat logic ---

  /**
   * Send the composed message (or `override` text, e.g. "Fix with AI").
   * `auto` marks automatic auto-fix follow-ups (they don't reset the budget).
   */
  private async sendMessage(override?: string, opts: { auto?: boolean } = {}): Promise<void> {
    if (this.currentAbort) return; // a reply is still streaming
    const text = override ?? this.textInput.value.trim();
    if (!text && this.pendingImages.length === 0) return;

    const settings = loadAISettings();
    if (!settings) return;
    if (!opts.auto) this.autoFixBudget = 2;

    // Optional screenshot of the 3D view
    const images = override ? [] : [...this.pendingImages];
    this.lastHadScreenshot = false;
    if (this.viewToggle.checked && PROVIDER_CONFIGS[settings.provider].supportsImages) {
      try {
        images.unshift(await resizeDataUrlToBase64(this.app.getViewport().captureImage(), 1024));
        this.lastHadScreenshot = true;
      } catch (e) {
        console.warn('View capture failed:', e);
      }
    }

    // Build message content
    let content: string | ContentBlock[];
    if (images.length > 0) {
      const blocks: ContentBlock[] = [];
      for (const img of images) {
        blocks.push({ type: 'image', imageData: img.data, mimeType: img.mimeType });
      }
      if (text) blocks.push({ type: 'text', text });
      content = blocks;
    } else {
      content = text;
    }

    const userMsg: AIMessage = { role: 'user', content };
    this.messages.push(userMsg);
    this.renderUserMessage(userMsg);

    // Clear input (an override message leaves the user's draft alone)
    if (override === undefined) {
      this.textInput.value = '';
      this.textInput.style.height = 'auto';
      this.pendingImages = [];
      this.imagePreviewContainer.innerHTML = '';
    }

    // Start streaming
    this.startStreaming(settings);
  }

  private startStreaming(settings: AISettings): void {
    // Build system prompt with context
    const code = this.app.getCode();
    const context = buildContextMessage(code || null, this.lastErrors.length > 0 ? this.lastErrors : null, {
      selection: this.app.getSelectionContext(),
      echo: this.lastEcho,
      hasScreenshot: this.lastHadScreenshot,
      renderSummary: this.app.getRenderSummary(),
    });
    const systemPrompt = buildSystemPrompt() + context;

    // Create assistant bubble
    const bubble = document.createElement('div');
    bubble.className = 'ai-msg ai-msg-assistant ai';
    const contentEl = document.createElement('div');
    contentEl.className = 'ai-msg-content';
    contentEl.innerHTML = '<span class="ai-cursor"></span>';
    bubble.appendChild(contentEl);
    this.messageList.appendChild(bubble);
    this.scrollToBottom();

    this.streamingEl = contentEl;
    this.streamingText = '';
    this.sendBtn.style.display = 'none';
    this.stopBtn.style.display = '';

    this.currentAbort = streamChat(
      settings,
      systemPrompt,
      this.messages,
      (chunk) => {
        this.streamingText += chunk;
        this.updateStreamingContent();
      },
      () => {
        this.finishStreaming();
      },
      (err) => {
        this.showError(err.message);
        this.finishStreaming();
      },
    );
  }

  private updateStreamingContent(): void {
    if (!this.streamingEl) return;
    this.streamingEl.innerHTML = this.formatContent(this.streamingText) + '<span class="ai-cursor"></span>';
    this.scrollToBottom();
  }

  private finishStreaming(): void {
    if (this.streamingEl) {
      // Remove cursor
      const cursor = this.streamingEl.querySelector('.ai-cursor');
      if (cursor) cursor.remove();

      // Add code action buttons
      this.streamingEl.innerHTML = this.formatContent(this.streamingText, true);
    }

    // Save assistant message
    if (this.streamingText) {
      this.messages.push({ role: 'assistant', content: this.streamingText });
    }

    this.streamingEl = null;
    this.streamingText = '';
    this.currentAbort = null;
    this.sendBtn.style.display = '';
    this.stopBtn.style.display = 'none';
    this.scrollToBottom();

    // Auto-save to history
    this.saveCurrentToHistory();
  }

  private stopStreaming(): void {
    if (this.currentAbort) {
      this.currentAbort.abort();
    }
  }

  private clearChat(): void {
    // Save current conversation before clearing
    this.saveCurrentToHistory();

    this.messages = [];
    this.messageList.innerHTML = '';
    this.pendingImages = [];
    this.imagePreviewContainer.innerHTML = '';
    this.currentConversationId = null;
    if (this.currentAbort) {
      this.currentAbort.abort();
    }
  }

  // --- Image handling ---

  private async handleImageUpload(): Promise<void> {
    const file = this.imageInput.files?.[0];
    if (!file) return;
    try {
      const result = await resizeImageToBase64(file);
      this.addPendingImage(result);
    } catch {
      this.showError('Failed to process image');
    }
    this.imageInput.value = '';
  }

  private async handleImagePaste(e: ClipboardEvent): Promise<void> {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        try {
          const result = await resizeImageToBase64(file);
          this.addPendingImage(result);
        } catch {
          this.showError('Failed to process pasted image');
        }
        return;
      }
    }
  }

  private addPendingImage(img: { data: string; mimeType: string }): void {
    this.pendingImages.push(img);
    const thumb = document.createElement('div');
    thumb.className = 'ai-image-thumb';
    const imgEl = document.createElement('img');
    imgEl.src = `data:${img.mimeType};base64,${img.data}`;
    thumb.appendChild(imgEl);
    const removeBtn = document.createElement('button');
    removeBtn.className = 'ai-thumb-remove';
    removeBtn.textContent = '\u00d7';
    removeBtn.addEventListener('click', () => {
      const idx = this.pendingImages.indexOf(img);
      if (idx >= 0) this.pendingImages.splice(idx, 1);
      thumb.remove();
    });
    thumb.appendChild(removeBtn);
    this.imagePreviewContainer.appendChild(thumb);
    this.textInput.focus();
    window.umami?.track('ai_image_upload');
  }

  // --- Rendering ---

  private renderUserMessage(msg: AIMessage): void {
    const bubble = document.createElement('div');
    bubble.className = 'ai-msg ai-msg-user user';
    const contentEl = document.createElement('div');
    contentEl.className = 'ai-msg-content';

    if (typeof msg.content === 'string') {
      contentEl.textContent = msg.content;
    } else {
      for (const block of msg.content) {
        if (block.type === 'image' && block.mimeType) {
          if (block.imageData) {
            const img = document.createElement('img');
            img.src = `data:${block.mimeType};base64,${block.imageData}`;
            img.className = 'ai-msg-image';
            contentEl.appendChild(img);
          } else {
            // Image placeholder (stripped from history)
            const placeholder = document.createElement('div');
            placeholder.className = 'ai-msg-image-placeholder';
            placeholder.textContent = '[Image]';
            contentEl.appendChild(placeholder);
          }
        } else if (block.type === 'text' && block.text) {
          const p = document.createElement('div');
          p.textContent = block.text;
          contentEl.appendChild(p);
        }
      }
    }

    bubble.appendChild(contentEl);
    this.messageList.appendChild(bubble);
    this.scrollToBottom();
  }

  private formatContent(text: string, addButtons: boolean = false): string {
    // Split by code blocks
    const parts: string[] = [];
    const codeBlockRegex = /```(?:scad|openscad)?\s*\n?([\s\S]*?)```/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let codeIndex = 0;

    while ((match = codeBlockRegex.exec(text)) !== null) {
      // Text before code block
      if (match.index > lastIndex) {
        const textPart = text.slice(lastIndex, match.index).trim();
        if (textPart) {
          parts.push(`<div class="ai-text">${this.escapeHtml(textPart)}</div>`);
        }
      }
      // Code block
      const code = match[1].trim();
      const id = `ai-code-${Date.now()}-${codeIndex++}`;
      let buttons = '';
      if (addButtons && code) {
        buttons = `<div class="ai-code-actions">
          <button class="ai-apply-btn" data-code-id="${id}">Apply</button>
          <button class="ai-insert-btn" data-code-id="${id}">Insert</button>
        </div>`;
      }
      parts.push(`<div class="ai-code-block"><pre id="${id}"><code>${this.highlightScad(code)}</code></pre>${buttons}</div>`);
      lastIndex = match.index + match[0].length;
    }

    // Remaining text after last code block
    if (lastIndex < text.length) {
      const remaining = text.slice(lastIndex).trim();
      if (remaining) {
        parts.push(`<div class="ai-text">${this.escapeHtml(remaining)}</div>`);
      }
    }

    // If no code blocks found, render as plain text
    if (parts.length === 0 && text.trim()) {
      parts.push(`<div class="ai-text">${this.escapeHtml(text)}</div>`);
    }

    const html = parts.join('');

    // Bind action buttons after rendering (deferred)
    if (addButtons) {
      setTimeout(() => this.bindCodeButtons(), 0);
    }

    return html;
  }

  // --- Syntax highlighting ---

  private highlightScad(code: string): string {
    const out: string[] = [];
    let i = 0;
    const len = code.length;

    while (i < len) {
      // Block comment
      if (i < len - 1 && code[i] === '/' && code[i + 1] === '*') {
        const end = code.indexOf('*/', i + 2);
        const slice = end >= 0 ? code.slice(i, end + 2) : code.slice(i);
        out.push(`<span class="sh-cmt">${this.escapeHtml(slice)}</span>`);
        i = end >= 0 ? end + 2 : len;
        continue;
      }

      // Line comment
      if (i < len - 1 && code[i] === '/' && code[i + 1] === '/') {
        const nl = code.indexOf('\n', i);
        const slice = nl >= 0 ? code.slice(i, nl) : code.slice(i);
        out.push(`<span class="sh-cmt">${this.escapeHtml(slice)}</span>`);
        i = nl >= 0 ? nl : len;
        continue;
      }

      // String
      if (code[i] === '"') {
        let j = i + 1;
        while (j < len) {
          if (code[j] === '\\') { j += 2; continue; }
          if (code[j] === '"') { j++; break; }
          j++;
        }
        out.push(`<span class="sh-str">${this.escapeHtml(code.slice(i, j))}</span>`);
        i = j;
        continue;
      }

      // Special variable ($fn, $fa, etc.)
      if (code[i] === '$') {
        let j = i + 1;
        while (j < len && /[a-zA-Z0-9_]/.test(code[j])) j++;
        if (j > i + 1) {
          out.push(`<span class="sh-var">${this.escapeHtml(code.slice(i, j))}</span>`);
          i = j;
          continue;
        }
      }

      // Number
      if (/[0-9]/.test(code[i]) || (code[i] === '.' && i + 1 < len && /[0-9]/.test(code[i + 1]))) {
        let j = i;
        // Integer part
        while (j < len && /[0-9]/.test(code[j])) j++;
        // Decimal part
        if (j < len && code[j] === '.') {
          j++;
          while (j < len && /[0-9]/.test(code[j])) j++;
        }
        // Exponent
        if (j < len && (code[j] === 'e' || code[j] === 'E')) {
          j++;
          if (j < len && (code[j] === '+' || code[j] === '-')) j++;
          while (j < len && /[0-9]/.test(code[j])) j++;
        }
        out.push(`<span class="sh-num">${this.escapeHtml(code.slice(i, j))}</span>`);
        i = j;
        continue;
      }

      // Word (identifier/keyword/builtin/constant)
      if (/[a-zA-Z_]/.test(code[i])) {
        let j = i;
        while (j < len && /[a-zA-Z0-9_]/.test(code[j])) j++;
        const word = code.slice(i, j);
        if (SH_KEYWORDS.has(word)) {
          out.push(`<span class="sh-kw">${word}</span>`);
        } else if (SH_CONSTANTS.has(word)) {
          out.push(`<span class="sh-const">${word}</span>`);
        } else if (SH_BUILTINS.has(word)) {
          out.push(`<span class="sh-fn">${word}</span>`);
        } else {
          out.push(this.escapeHtml(word));
        }
        i = j;
        continue;
      }

      // Single character (operator, punctuation, whitespace, etc.)
      out.push(this.escapeHtml(code[i]));
      i++;
    }

    return out.join('');
  }

  private bindCodeButtons(): void {
    this.messageList.querySelectorAll('.ai-apply-btn').forEach(btn => {
      if ((btn as any)._bound) return;
      (btn as any)._bound = true;
      btn.addEventListener('click', () => {
        const codeId = (btn as HTMLElement).dataset.codeId!;
        const pre = document.getElementById(codeId);
        if (pre) {
          const code = pre.textContent || '';
          this.applyCode(code, pre.closest('.ai-code-block') as HTMLElement | null);
        }
      });
    });
    this.messageList.querySelectorAll('.ai-insert-btn').forEach(btn => {
      if ((btn as any)._bound) return;
      (btn as any)._bound = true;
      btn.addEventListener('click', () => {
        const codeId = (btn as HTMLElement).dataset.codeId!;
        const pre = document.getElementById(codeId);
        if (pre) {
          const code = pre.textContent || '';
          this.insertCode(code);
        }
      });
    });
  }

  /** Show a diff of the AI code against the editor; apply on confirm (undoable). */
  private applyCode(code: string, anchor: HTMLElement | null): void {
    const current = this.app.getCode();
    // Code blocks are trimmed — keep the editor's trailing newline so it
    // doesn't show up as a change
    if (current.endsWith('\n') && !code.endsWith('\n')) code += '\n';
    if (!current.trim()) { this.commitApply(code); return; }
    const lines = diffLines(current, code);
    const { added, removed } = diffStats(lines);
    anchor?.parentElement?.querySelector('.ai-diff')?.remove();
    if (added === 0 && removed === 0) {
      this.showNotice(anchor, 'No changes — the editor already has this code.');
      return;
    }
    const box = document.createElement('div');
    box.className = 'ai-diff';
    const head = document.createElement('div');
    head.className = 'ai-diff-head';
    head.innerHTML = `<span>Changes <b class="ai-diff-add">+${added}</b> <b class="ai-diff-del">−${removed}</b></span>`;
    const body = document.createElement('div');
    body.className = 'ai-diff-body';
    for (const l of diffHunks(lines, 2)) {
      const row = document.createElement('div');
      if (!l) { row.className = 'ai-diff-gap'; row.textContent = '⋯'; }
      else {
        row.className = l.kind === '+' ? 'ai-diff-line add' : l.kind === '-' ? 'ai-diff-line del' : 'ai-diff-line';
        row.textContent = `${l.kind === ' ' ? ' ' : l.kind} ${l.text}`;
      }
      body.appendChild(row);
    }
    const actions = document.createElement('div');
    actions.className = 'ai-diff-actions';
    const ok = document.createElement('button');
    ok.className = 'ai-apply-btn ai-diff-confirm';
    ok.textContent = 'Apply change';
    const cancel = document.createElement('button');
    cancel.className = 'ai-insert-btn ai-diff-cancel';
    cancel.textContent = 'Cancel';
    ok.addEventListener('click', () => { box.remove(); this.commitApply(code); });
    cancel.addEventListener('click', () => box.remove());
    actions.append(cancel, ok);
    box.append(head, body, actions);
    if (anchor) anchor.insertAdjacentElement('afterend', box);
    else this.messageList.appendChild(box);
    box.scrollIntoView({ block: 'nearest' });
  }

  private commitApply(code: string): void {
    // One minimal undoable edit (Ctrl+Z in the editor restores the old code)
    this.app.applyCodeChange(code);
    this.awaitingAppliedRender = true;
    window.umami?.track('ai_apply_code');
  }

  private showNotice(anchor: HTMLElement | null, text: string): void {
    const n = document.createElement('div');
    n.className = 'ai-diff ai-diff-note';
    n.textContent = text;
    if (anchor) anchor.insertAdjacentElement('afterend', n);
    else this.messageList.appendChild(n);
    setTimeout(() => n.remove(), 2500);
  }

  private insertCode(code: string): void {
    // Below the cursor line, as one undoable edit
    this.app.insertAtCursor(code);
    window.umami?.track('ai_apply_code', { mode: 'insert' });
  }

  private showError(message: string): void {
    const el = document.createElement('div');
    el.className = 'ai-msg ai-msg-error';
    el.textContent = message;
    this.messageList.appendChild(el);
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    this.messageList.scrollTop = this.messageList.scrollHeight;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // --- History persistence ---

  private loadHistory(): ConversationSummary[] {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  }

  private saveHistory(history: ConversationSummary[]): void {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch { /* quota exceeded — silently fail */ }
  }

  private stripImagesForStorage(messages: AIMessage[]): AIMessage[] {
    return messages.map(msg => {
      if (typeof msg.content === 'string') return msg;
      const stripped: ContentBlock[] = msg.content.map(block => {
        if (block.type === 'image') {
          // Keep mimeType for placeholder, drop imageData
          return { type: 'image' as const, mimeType: block.mimeType };
        }
        return block;
      });
      return { ...msg, content: stripped };
    });
  }

  private getConversationTitle(): string {
    for (const msg of this.messages) {
      if (msg.role === 'user') {
        const text = typeof msg.content === 'string'
          ? msg.content
          : msg.content.find(b => b.type === 'text')?.text || '';
        if (text) return text.length > 80 ? text.slice(0, 77) + '...' : text;
      }
    }
    return 'New conversation';
  }

  private saveCurrentToHistory(): void {
    if (this.messages.length === 0) return;

    const history = this.loadHistory();

    const entry: ConversationSummary = {
      id: this.currentConversationId || (Date.now().toString(36) + Math.random().toString(36).slice(2, 6)),
      title: this.getConversationTitle(),
      timestamp: Date.now(),
      messages: this.stripImagesForStorage(this.messages),
    };

    // Upsert
    const existIdx = history.findIndex(h => h.id === entry.id);
    if (existIdx >= 0) {
      history[existIdx] = entry;
    } else {
      history.unshift(entry);
    }

    this.currentConversationId = entry.id;

    // Trim to max
    if (history.length > MAX_HISTORY) {
      history.length = MAX_HISTORY;
    }

    this.saveHistory(history);
  }

  private deleteFromHistory(id: string): void {
    const history = this.loadHistory().filter(h => h.id !== id);
    this.saveHistory(history);
    // If the deleted conversation is the current one, clear the id
    if (this.currentConversationId === id) {
      this.currentConversationId = null;
    }
    this.renderHistoryList();
  }

  private loadConversation(conv: ConversationSummary): void {
    // Save current first
    this.saveCurrentToHistory();

    // Abort any streaming
    if (this.currentAbort) {
      this.currentAbort.abort();
      this.currentAbort = null;
      this.streamingEl = null;
      this.streamingText = '';
      this.sendBtn.style.display = '';
      this.stopBtn.style.display = 'none';
    }

    // Load
    this.messages = conv.messages.map(m => ({ ...m }));
    this.currentConversationId = conv.id;
    this.pendingImages = [];
    this.imagePreviewContainer.innerHTML = '';

    // Re-render messages
    this.messageList.innerHTML = '';
    for (const msg of this.messages) {
      if (msg.role === 'user') {
        this.renderUserMessage(msg);
      } else {
        const bubble = document.createElement('div');
        bubble.className = 'ai-msg ai-msg-assistant ai';
        const contentEl = document.createElement('div');
        contentEl.className = 'ai-msg-content';
        contentEl.innerHTML = this.formatContent(typeof msg.content === 'string' ? msg.content : '', true);
        bubble.appendChild(contentEl);
        this.messageList.appendChild(bubble);
      }
    }
    this.scrollToBottom();

    // Switch to chat tab
    this.switchTab('chat');
  }

  private renderHistoryList(): void {
    const history = this.loadHistory();
    if (history.length === 0) {
      this.historyList.innerHTML = '<div class="ai-history-empty">No conversation history</div>';
      return;
    }

    this.historyList.innerHTML = '';
    for (const conv of history) {
      const item = document.createElement('div');
      item.className = 'ai-history-item';
      if (conv.id === this.currentConversationId) {
        item.classList.add('ai-history-current');
      }

      const info = document.createElement('div');
      info.className = 'ai-history-info';
      info.addEventListener('click', () => this.loadConversation(conv));

      const title = document.createElement('div');
      title.className = 'ai-history-title';
      title.textContent = conv.title;
      info.appendChild(title);

      const time = document.createElement('div');
      time.className = 'ai-history-time';
      time.textContent = this.formatTimestamp(conv.timestamp);
      info.appendChild(time);

      item.appendChild(info);

      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'ai-history-delete';
      deleteBtn.textContent = '\u00d7';
      deleteBtn.title = 'Delete';
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.deleteFromHistory(conv.id);
      });
      item.appendChild(deleteBtn);

      this.historyList.appendChild(item);
    }
  }

  private formatTimestamp(ts: number): string {
    const date = new Date(ts);
    const now = new Date();
    const isToday = date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}

// --- Settings Modal ---

export function createSettingsModal(): {
  overlay: HTMLElement;
  open: () => void;
  close: () => void;
  onSave: (callback: (settings: AISettings | null) => void) => void;
} {
  const overlay = document.createElement('div');
  overlay.id = 'ai-settings-modal-overlay';

  // Build provider options from config
  const providerOptions = (Object.keys(PROVIDER_CONFIGS) as ProviderType[])
    .map(k => `<option value="${k}">${PROVIDER_CONFIGS[k].label}</option>`)
    .join('\n            ');

  overlay.innerHTML = `
    <div id="ai-settings-modal">
      <div id="ai-settings-header">
        <h2>AI Settings</h2>
        <button id="ai-settings-close">&times;</button>
      </div>
      <div id="ai-settings-body">
        <div class="ai-settings-row">
          <label for="ai-provider">Provider</label>
          <select id="ai-provider">
            ${providerOptions}
          </select>
          <p class="ai-provider-hint" id="ai-provider-hint"></p>
        </div>
        <div class="ai-settings-row" id="ai-api-key-row">
          <label for="ai-api-key">API Key</label>
          <input type="password" id="ai-api-key" placeholder="Enter API key..." autocomplete="off">
        </div>
        <div class="ai-settings-row">
          <label for="ai-model">Model</label>
          <input type="text" id="ai-model" placeholder="Model name...">
        </div>
        <div class="ai-settings-row" id="ai-base-url-row" style="display:none">
          <label for="ai-base-url">Base URL</label>
          <input type="text" id="ai-base-url" placeholder="https://your-server.com/v1" autocomplete="off">
        </div>
        <div class="ai-settings-actions">
          <button id="ai-test-btn">Test Connection</button>
          <span id="ai-test-status"></span>
        </div>
        <div class="ai-settings-footer">
          <button id="ai-save-btn">Save</button>
          <button id="ai-disable-btn">Disable AI</button>
        </div>
        <p class="ai-settings-note">Your API key is stored in your browser's localStorage and never sent to our servers.</p>
        <p class="ai-settings-mcp">No API key? Use SCAD+ from <strong>Claude Desktop</strong> or <strong>Claude Code</strong> with your subscription &mdash; <a href="#" id="ai-mcp-link">how to connect (MCP)</a></p>
      </div>
    </div>
  `;

  const providerSelect = overlay.querySelector('#ai-provider') as HTMLSelectElement;
  const apiKeyInput = overlay.querySelector('#ai-api-key') as HTMLInputElement;
  const apiKeyRow = overlay.querySelector('#ai-api-key-row') as HTMLElement;
  const modelInput = overlay.querySelector('#ai-model') as HTMLInputElement;
  const baseUrlInput = overlay.querySelector('#ai-base-url') as HTMLInputElement;
  const baseUrlRow = overlay.querySelector('#ai-base-url-row') as HTMLElement;
  const providerHint = overlay.querySelector('#ai-provider-hint') as HTMLElement;
  const testBtn = overlay.querySelector('#ai-test-btn') as HTMLButtonElement;
  const testStatus = overlay.querySelector('#ai-test-status') as HTMLElement;
  const saveBtn = overlay.querySelector('#ai-save-btn') as HTMLButtonElement;
  const disableBtn = overlay.querySelector('#ai-disable-btn') as HTMLButtonElement;
  const closeBtn = overlay.querySelector('#ai-settings-close') as HTMLButtonElement;

  let saveCallback: ((settings: AISettings | null) => void) | null = null;

  // Help → "Claude (MCP)" tab (wired in main.ts via the Help menu item)
  overlay.querySelector('#ai-mcp-link')!.addEventListener('click', (e) => {
    e.preventDefault();
    closeBtn.click();
    document.getElementById('menu-mcp')?.click();
  });

  // Collect all known default models for auto-switch detection
  const allDefaults = new Set(Object.values(PROVIDER_CONFIGS).map(c => c.defaultModel).filter(Boolean));

  // Update UI based on selected provider
  function updateProviderUI(provider: ProviderType): void {
    const config = PROVIDER_CONFIGS[provider];
    providerHint.textContent = config.hint;

    // Show/hide API key row
    apiKeyRow.style.display = config.needsAuth ? '' : 'none';

    // Show/hide base URL row
    const showBaseUrl = provider === 'custom' || provider === 'ollama' || provider === 'lmstudio';
    baseUrlRow.style.display = showBaseUrl ? '' : 'none';
    if (provider === 'ollama') {
      baseUrlInput.placeholder = 'http://localhost:11434/v1';
    } else if (provider === 'lmstudio') {
      baseUrlInput.placeholder = 'http://localhost:1234/v1';
    } else if (provider === 'custom') {
      baseUrlInput.placeholder = 'https://your-server.com/v1';
    }
  }

  // Load existing settings
  function loadIntoForm(): void {
    const settings = loadAISettings();
    if (settings) {
      providerSelect.value = settings.provider;
      apiKeyInput.value = settings.apiKey || '';
      modelInput.value = settings.model;
      baseUrlInput.value = settings.baseUrl || '';
    } else {
      providerSelect.value = 'claude';
      apiKeyInput.value = '';
      modelInput.value = getDefaultModel('claude');
      baseUrlInput.value = '';
    }
    updateProviderUI(providerSelect.value as ProviderType);
    testStatus.textContent = '';
    testStatus.className = '';
  }

  // Update default model when provider changes
  providerSelect.addEventListener('change', () => {
    const provider = providerSelect.value as ProviderType;
    // Auto-switch model if current is any known default
    const currentModel = modelInput.value;
    if (!currentModel || allDefaults.has(currentModel)) {
      modelInput.value = getDefaultModel(provider);
    }
    baseUrlInput.value = '';
    updateProviderUI(provider);
    testStatus.textContent = '';
    testStatus.className = '';
  });

  // Test connection
  testBtn.addEventListener('click', async () => {
    const provider = providerSelect.value as ProviderType;
    const config = PROVIDER_CONFIGS[provider];
    const key = apiKeyInput.value.trim();
    if (config.needsAuth && !key) {
      testStatus.textContent = 'Enter an API key first';
      testStatus.className = 'ai-test-error';
      return;
    }
    if (provider === 'custom' && !baseUrlInput.value.trim()) {
      testStatus.textContent = 'Enter a base URL first';
      testStatus.className = 'ai-test-error';
      return;
    }
    testBtn.disabled = true;
    testStatus.textContent = 'Testing...';
    testStatus.className = '';

    const settings: AISettings = {
      provider,
      apiKey: key,
      model: modelInput.value || getDefaultModel(provider),
      baseUrl: baseUrlInput.value.trim() || undefined,
    };

    const result = await validateKey(settings);
    testBtn.disabled = false;
    if (result.ok) {
      testStatus.textContent = 'Connection successful!';
      testStatus.className = 'ai-test-success';
    } else {
      testStatus.textContent = result.error || 'Connection failed';
      testStatus.className = 'ai-test-error';
    }
  });

  // Save
  saveBtn.addEventListener('click', () => {
    const provider = providerSelect.value as ProviderType;
    const config = PROVIDER_CONFIGS[provider];
    const key = apiKeyInput.value.trim();
    if (config.needsAuth && !key) {
      testStatus.textContent = 'Enter an API key';
      testStatus.className = 'ai-test-error';
      return;
    }
    if (provider === 'custom' && !baseUrlInput.value.trim()) {
      testStatus.textContent = 'Enter a base URL';
      testStatus.className = 'ai-test-error';
      return;
    }
    const settings: AISettings = {
      provider,
      apiKey: key,
      model: modelInput.value || getDefaultModel(provider),
      baseUrl: baseUrlInput.value.trim() || undefined,
    };
    saveAISettings(settings);
    close();
    saveCallback?.(settings);
  });

  // Disable
  disableBtn.addEventListener('click', () => {
    clearAISettings();
    close();
    saveCallback?.(null);
  });

  // Close
  function open(): void {
    loadIntoForm();
    overlay.classList.add('open');
  }

  function close(): void {
    overlay.classList.remove('open');
  }

  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  document.body.appendChild(overlay);

  return {
    overlay,
    open,
    close,
    onSave: (cb) => { saveCallback = cb; },
  };
}
